import React, { useState } from "react";
import {
  Download,
  AlertCircle,
  CheckCircle,
  Clock,
  ExternalLink,
  Package,
  CalendarRange,
  Info,
} from "lucide-react";
import { toast } from "react-toastify";
import MobileCard from "../mobileCard/MobileCard";
import MobileButton from "../mobileButton/MobileButton";
import {
  requestDownloadAll,
  requestDownloadByDateRange,
  pollDownloadStatus,
} from "../../api/downloadImages.api";

const NOTES = [
  "No cierres esta pestaña hasta que la descarga haya finalizado, o se cancelará.",
  "Puede tomar varios minutos. El estado se verifica automáticamente durante 15 minutos.",
  "Solo se puede realizar una descarga por mes. Si ya descargaste este mes, contacta a soporte o esperá al próximo mes.",
];

const Downloads = ({ user }) => {
  const [downloadMode, setDownloadMode] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);

  if (!user?.feedID) {
    return (
      <MobileCard className="bg-amber-50 border-amber-200">
        <p className="text-amber-700 text-sm text-center">Este proyecto no tiene un Feed ID configurado para descargar imágenes.</p>
      </MobileCard>
    );
  }

  const handleDownloadAll = async () => {
    if (!user.customerId || !user.feedID) {
      toast.error("Datos de la cámara incompletos");
      return;
    }

    setIsLoading(true);
    try {
      const response = await requestDownloadAll(user.customerId, user.feedID);

      if (response.estado === "ERROR") {
        toast.error(response.mensajeError || "Error al solicitar la descarga");
        setIsLoading(false);
        return;
      }

      if (response.jobId) {
        setDownloadStatus("En progreso...");
        toast.info("Descarga iniciada. Verificando estado...");

        try {
          const finalStatus = await pollDownloadStatus(
            user.customerId,
            user.feedID,
            "all"
          );
          setDownloadStatus(finalStatus);
          toast.success("Descarga completada");
        } catch (error) {
          toast.warning(
            "La descarga está en progreso. Puede cerrar este modal."
          );
          setDownloadStatus(error.message);
        }
      }
    } catch (error) {
      console.error("Error downloading all images:", error);
      const errorMessage =
        "No es posible realizar la descarga ahora. Si ya realizó una descarga este mes, debe contactar a soporte o esperar al próximo mes.";
      toast.error(errorMessage);
      setDownloadStatus(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadByRange = async () => {
    if (!dateFrom || !dateTo) {
      toast.error("Selecciona ambas fechas");
      return;
    }

    if (!user.customerId || !user.feedID) {
      toast.error("Datos de la cámara incompletos");
      return;
    }

    if (new Date(dateFrom) > new Date(dateTo)) {
      toast.error("La fecha inicial no puede ser mayor que la final");
      return;
    }

    setIsLoading(true);
    try {
      const response = await requestDownloadByDateRange(
        user.customerId,
        user.feedID,
        dateFrom,
        dateTo
      );

      if (response.estado === "ERROR") {
        toast.error(response.mensajeError || "Error al solicitar la descarga");
        setIsLoading(false);
        return;
      }

      if (response.estado === "EXISTE_RANGO_PROCESANDO") {
        toast.warning("Ya existe una descarga en rango en progreso para este mes");
        setIsLoading(false);
        return;
      }

      if (response.jobId) {
        setDownloadStatus("En progreso...");
        toast.info("Descarga por rango iniciada. Verificando estado...");

        try {
          const finalStatus = await pollDownloadStatus(
            user.customerId,
            user.feedID,
            "range"
          );
          setDownloadStatus(finalStatus);
          toast.success("Descarga completada");
        } catch (error) {
          toast.warning(
            "La descarga está en progreso. Puede cerrar este modal."
          );
          setDownloadStatus(error.message);
        }
      }
    } catch (error) {
      console.error("Error downloading by date range:", error);
      const errorMessage =
        "No es posible realizar la descarga ahora. Si ya realizó una descarga este mes, debe contactar a soporte o esperar al próximo mes.";
      toast.error(errorMessage);
      setDownloadStatus(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const canSubmit = downloadMode === "all" || (dateFrom && dateTo);
  const hasReadyDownload =
    downloadStatus &&
    typeof downloadStatus !== "string" &&
    (downloadStatus.urlFirmada || downloadStatus.downloadUrl);

  return (
    <div className="space-y-4 md:space-y-6 animate-fadeIn">
      {/* Hero header */}
      <MobileCard className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200" padding="p-4 md:p-6">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
            <Download className="w-6 h-6 md:w-7 md:h-7 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg md:text-2xl font-bold text-gray-800 truncate">Descargar Imágenes</h2>
            <p className="text-xs md:text-sm text-gray-600 truncate">
              Cámara: <span className="font-semibold">{user?.Name || user?.projectName || user.feedID}</span>
            </p>
          </div>
        </div>
      </MobileCard>

      <MobileCard padding="p-4 md:p-6">
        <div className="space-y-5 md:space-y-6">
          {/* Mode Selection - segmented cards */}
          <div className="space-y-2 md:space-y-3">
            <label className="block text-sm font-semibold text-gray-700">
              Tipo de Descarga
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setDownloadMode("all")}
                disabled={isLoading}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                  downloadMode === "all"
                    ? "border-blue-500 bg-blue-50 shadow-sm"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                  downloadMode === "all" ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-500"
                }`}>
                  <Package className="w-5 h-5" />
                </div>
                <div>
                  <p className={`text-sm font-semibold ${downloadMode === "all" ? "text-blue-700" : "text-gray-700"}`}>
                    Descargar Todo
                  </p>
                  <p className="text-xs text-gray-500">Todas las imágenes disponibles</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setDownloadMode("range")}
                disabled={isLoading}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                  downloadMode === "range"
                    ? "border-blue-500 bg-blue-50 shadow-sm"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                  downloadMode === "range" ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-500"
                }`}>
                  <CalendarRange className="w-5 h-5" />
                </div>
                <div>
                  <p className={`text-sm font-semibold ${downloadMode === "range" ? "text-blue-700" : "text-gray-700"}`}>
                    Rango de Fechas
                  </p>
                  <p className="text-xs text-gray-500">Elegí un período específico</p>
                </div>
              </button>
            </div>
          </div>

          {/* Date Range Selector */}
          {downloadMode === "range" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 animate-fadeIn">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Fecha Desde
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Fecha Hasta
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoading}
                />
              </div>
            </div>
          )}

          {/* Status Display */}
          {downloadStatus && (
            <div className={`p-4 rounded-xl border animate-fadeIn ${
              typeof downloadStatus === "string"
                ? "bg-blue-50 border-blue-200"
                : downloadStatus.estado === "LISTO" || downloadStatus.estado === "COMPLETADO"
                ? "bg-green-50 border-green-200"
                : downloadStatus.estado === "ERROR"
                ? "bg-red-50 border-red-200"
                : "bg-blue-50 border-blue-200"
            }`}>
              {typeof downloadStatus === "string" ? (
                <div className="flex items-start gap-3">
                  {downloadStatus.includes("Error") ? (
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  ) : downloadStatus.includes("completada") ? (
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Clock className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5 animate-pulse" />
                  )}
                  <div className={`text-sm ${
                    downloadStatus.includes("Error")
                      ? "text-red-900"
                      : downloadStatus.includes("completada")
                      ? "text-green-900"
                      : "text-blue-900"
                  }`}>
                    {downloadStatus}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    {downloadStatus.estado === "LISTO" || downloadStatus.estado === "COMPLETADO" ? (
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    ) : downloadStatus.estado === "ERROR" ? (
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    ) : (
                      <Clock className="w-5 h-5 text-blue-500 flex-shrink-0 animate-pulse" />
                    )}
                    <p className={`font-semibold text-sm ${
                      downloadStatus.estado === "LISTO" || downloadStatus.estado === "COMPLETADO"
                        ? "text-green-900"
                        : downloadStatus.estado === "ERROR"
                        ? "text-red-900"
                        : "text-blue-900"
                    }`}>
                      {downloadStatus.estado}
                    </p>
                  </div>

                  {(downloadStatus.urlFirmada || downloadStatus.downloadUrl) && (
                    <a
                      href={downloadStatus.urlFirmada || downloadStatus.downloadUrl}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Descargar archivo ZIP
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}

                  {downloadStatus.mensajeError && (
                    <p className="text-sm text-red-900">
                      <strong>Error:</strong> {downloadStatus.mensajeError}
                    </p>
                  )}

                  {downloadStatus.mensaje && (
                    <p className="text-sm text-gray-700">
                      <strong>Mensaje:</strong> {downloadStatus.mensaje}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Consolidated info panel */}
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span className="text-sm font-semibold text-amber-800">Antes de descargar</span>
            </div>
            <ul className="space-y-1.5">
              {NOTES.map((note, idx) => (
                <li key={idx} className="flex items-start gap-2 text-xs md:text-sm text-amber-800">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Action Button */}
          {!hasReadyDownload && (
            <MobileButton
              variant="primary"
              size="lg"
              fullWidth
              loading={isLoading}
              disabled={!canSubmit}
              onClick={downloadMode === "all" ? handleDownloadAll : handleDownloadByRange}
              icon={<Download className="w-5 h-5" />}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
            >
              Descargar
            </MobileButton>
          )}
        </div>
      </MobileCard>
    </div>
  );
};

export default Downloads;
